import { useNavigate } from 'react-router-dom';
import {
  Clock, CheckCircle2, Circle, Lightbulb, AlertTriangle, ArrowRight, Info, MinusCircle, RotateCcw,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';

const TIER_STYLE = {
  critical: { label: 'Critical', cls: 'bg-[#FBE9E7] text-[#C0492E]' },
  high: { label: 'Important', cls: 'bg-[#FCF3DC] text-[#9A7B1F]' },
  medium: { label: 'Recommended', cls: 'bg-[#EAF2F9] text-[#3A6EA5]' },
  low: { label: 'Nice to have', cls: 'bg-[#F0EFEC] text-[#8D8880]' },
};

export default function ReadinessItemDialog({ item, open, onClose, canEdit, onSetApplicable }) {
  const navigate = useNavigate();
  if (!item) return null;
  const tier = TIER_STYLE[item.tier] || TIER_STYLE.medium;
  const done = item.status === 'completed';
  const na = item.status === 'not_applicable';

  const go = () => { onClose?.(); navigate(item.link); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg rounded-3xl bg-white p-0 overflow-hidden" data-testid="readiness-item-dialog">
        <div className="px-6 pt-6 pb-4 border-b border-[#EEECE8]">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${tier.cls}`}>{tier.label}</span>
              <span className="text-xs text-[#8D8880] flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> ~{item.estimated_minutes} min</span>
            </div>
            <DialogTitle className="text-2xl font-bold text-[#2C3A47] text-left leading-snug">{item.title}</DialogTitle>
            <DialogDescription className="sr-only">Why this task matters and where to complete it.</DialogDescription>
          </DialogHeader>
          <div className="mt-3" data-testid="readiness-item-status">
            {na ? (
              <span data-status="not_applicable"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl font-semibold text-sm bg-[#F3F1EE] text-[#8D8880]">
                <MinusCircle className="h-5 w-5" /> Not applicable — excluded from your score
              </span>
            ) : (
              <span data-status={item.status}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-2xl font-semibold text-sm ${done ? 'bg-[#EAF2EC] text-[#5E8468]' : 'bg-[#F3F1EE] text-[#8D8880]'}`}>
                {done ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
                {done ? `Complete — added in ${item.section_label}` : `Not started — add it in ${item.section_label}`}
              </span>
            )}
          </div>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[55vh] overflow-y-auto">
          <div>
            <p className="text-sm font-semibold text-[#57534E] mb-1 flex items-center gap-1.5"><Info className="h-4 w-4 text-[#3A6EA5]" /> Why this matters</p>
            <p className="text-[15px] text-[#4A5560] leading-relaxed">{item.why_it_matters}</p>
          </div>

          {item.tips?.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-[#57534E] mb-1 flex items-center gap-1.5"><Lightbulb className="h-4 w-4 text-[#7FA98C]" /> Tips</p>
              <ul className="list-disc pl-5 space-y-1 text-[15px] text-[#4A5560]">
                {item.tips.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            </div>
          )}

          {item.common_mistakes?.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-[#57534E] mb-1 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 text-[#E9B949]" /> Avoid these mistakes</p>
              <ul className="list-disc pl-5 space-y-1 text-[15px] text-[#4A5560]">
                {item.common_mistakes.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          )}

          {canEdit && (
            na ? (
              <button data-testid="readiness-restore-item" onClick={() => onSetApplicable?.(item.id, true)}
                className="w-full flex items-center justify-center gap-2 text-sm font-medium text-[#3A6EA5] bg-[#F1F6FB] hover:bg-[#E1EAF3] rounded-xl py-2.5 transition-colors">
                <RotateCcw className="h-4 w-4" /> Restore — count this task again
              </button>
            ) : (
              <button data-testid="readiness-dismiss-item" onClick={() => onSetApplicable?.(item.id, false)}
                className="w-full flex items-center justify-center gap-2 text-sm font-medium text-[#8D8880] hover:text-[#C0492E] bg-[#F7F6F4] hover:bg-[#FBE9E7] rounded-xl py-2.5 transition-colors">
                <MinusCircle className="h-4 w-4" /> This doesn't apply to me
              </button>
            )
          )}

          {item.disclaimer && <p className="text-xs text-[#9C948B] border-t border-[#F2F1EE] pt-3">{item.disclaimer}</p>}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#EEECE8] px-6 py-4 flex gap-3">
          <Button variant="outline" onClick={onClose} className="rounded-full flex-1">Close</Button>
          {!na && (
            <Button data-testid="readiness-goto-section" onClick={go}
              className="rounded-full flex-1 bg-[#4F86C6] hover:bg-[#3A6EA5] text-white">
              {done ? `Review in ${item.section_label}` : `Go to ${item.section_label}`} <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
